import {
  getControllerCandidateEdit
} from '../components/Candidates/Edit/CandidateEdit';
import {
  Arr
} from './Array.lib';

/**
 * Determines the ownership status of a job candidate and returns
 * corresponding text and color indicators.
 *
 * @function getJobOwnershipTextAndColor
 * @param {Object[]} ownershipResult - Ownership result array containing
 *   candidate ownership info.
 * @param {string|number} employerId - Employer identifier.
 * @param {string|number} currentCandoId - Current candidate identifier.
 * @param {string|number|null} [jobId=null] - Job identifier, optional.
 * @param {Object} identifiedDuplicateCando - Candidate identified as a
 *   duplicate, may be empty.
 * @param {boolean} [isCandoUniqDirty=false] - Flag to control uniqueness
 *   validation state.
 * @returns {Object} Object containing:
 *   - {string} color - Color code based on ownership state.
 *   - {string} label - Text label for ownership state.
 *   - {Object[]} isOwned - Candidates marked as owned by another employer.
 *   - {Object|null} iOwnIt - Current employer's owned candidate record.
 *   - {Object[]} isTbd - Candidates marked as TBD.
 *   - {boolean} hasEngagement - True if candidate has job engagement.
 *   - {boolean} iTbdItWithoutEngagement - True if TBD but no engagement.
 *
 * @description
 * - Filters and categorizes ownership results based on employer and
 *   candidate IDs.
 * - Assigns colors and labels based on whether the candidate is owned,
 *   TBD, available, or already owned by the current employer.
 * - Supports checking duplicate candidates and engagement state.
 */
export default function getJobOwnershipTextAndColor(
  ownershipResult,
  employerId,
  currentCandoId,
  jobId = null,
  identifiedDuplicateCando,
  isCandoUniqDirty = false
) {

  // Normalize ownershipResult into an array
  ownershipResult = Arr(ownershipResult);

  // Extract stats for the current candidate
  const myStats = ownershipResult.filter(
    (obj) => String(obj.candidateId) === String(currentCandoId)
  );

  // Exclude current candidate from ownershipResult
  ownershipResult = ownershipResult.filter(
    (obj) => String(obj.candidateId) !== String(currentCandoId)
  );

  let duplicateIds = [];
  try {
    const ctrl = getControllerCandidateEdit();
    if (ctrl?.state?.potentialDuplicatedCandidateList?.length) {
      duplicateIds = ctrl.state.potentialDuplicatedCandidateList.map(d => String(d.id));
    }
  }
  catch {
    // do nothing if controller is not available
  }

  // Get conflicts for this employer only where conflict is strong
  const forThisEmployerOnly = ownershipResult.filter(
    (obj) =>
      (
        !duplicateIds.length
        || (
          duplicateIds.includes(String(obj.candidateId))
          || String(obj.candidateId) === String(currentCandoId)
        )
      ) &&
      !!obj.conflictedEmployers.find(
        (obj) => String(obj.employerId) === String(employerId)
      ) &&
      !!obj.isPotentialStrongConflict
  );

  // Filter conflicts where ownership is marked as "owned"
  let isOwned = forThisEmployerOnly.filter(
    (obj) =>
      !!obj.conflictedEmployers.find(
        (obj) =>
          String(obj.employerId) === String(employerId) &&
          obj.hasOwnership === 'owned'
      )
  );

  // Filter conflicts where ownership is marked as "tbd"
  let isTbd = forThisEmployerOnly.filter(
    (obj) =>
      !!obj.conflictedEmployers.find(
        (obj) =>
          String(obj.employerId) === String(employerId) &&
          obj.hasOwnership === 'tbd'
      )
  );

  // Default values assume candidate is taken by others
  let color = colors.orange.darker; // TAKEN
  let label = 'OwnedByOther';
  let hasEngagement = false;

  // Check conflicts against identified duplicate candidate if present
  const identifiedDuplicateCandoId = Object(identifiedDuplicateCando).id;
  if (!!identifiedDuplicateCandoId) {
    isOwned = isOwned.filter(
      (obj) =>
        String(obj.candidateId) === String(identifiedDuplicateCandoId) ||
        String(currentCandoId) === String(obj.candidateId)
    );
    isTbd = isTbd.filter(
      (obj) =>
        String(obj.candidateId) === String(identifiedDuplicateCandoId) ||
        String(currentCandoId) === String(obj.candidateId)
    );
  }

  // Check if account owns this candidate for this employer
  let iOwnIt = myStats.find(
    (obj) =>
      !!obj.conflictedEmployers.find(
        (obj) =>
          String(obj.employerId) === String(employerId) &&
          obj.hasOwnership === 'owned'
      )
  );

  // Check if employer is marked "tbd" for this candidate for the current account
  let iTbdIt = myStats.find(
    (obj) =>
      !!obj.conflictedEmployers.find(
        (obj) =>
          String(obj.employerId) === String(employerId) &&
          obj.hasOwnership === 'tbd'
      )
  );

  // If no other account owns this employer for this account
  if (!isOwned.length) {
    if (!!isTbd.length || !!iTbdIt) {
      color = colors.gray.common; // TBD
      label = 'TbdForOther';
    }
    else {
      color = colors.purple.common; // AVAILABLE
      label = 'OwnedByNoOne';
    }
  }

  // Check if current candidate has any engagement for this job
  if (!!myStats.length) {
    hasEngagement = myStats[0].conflictedEmployers.find(
      (obj) => !!obj.hasEngagement && String(obj.jobId) === String(jobId)
    );
  }

  // Flag if candidate is TBD but without engagement
  let iTbdItWithoutEngagement = false;

  // Only check TBD without engagement if candidate uniqueness is not dirty
  if (!isCandoUniqDirty) {
    iTbdItWithoutEngagement = iTbdIt && !hasEngagement;

    // If current employer already owns the candidate
    if (!!iOwnIt) {
      color = colors.cyan.darker; // YOUR
      label = 'iAlreadyOwnIt';
    }
    // If current employer marked as TBD and no engagement
    else if (iTbdItWithoutEngagement) {
      color = colors.gray.common; // TBD
      label = 'TbdForCando';
    }
  }

  console.debug('getJobOwnershipTextAndColor', {
    // INPUTS
    ownershipResult,
    employerId,
    currentCandoId,
    jobId,
    identifiedDuplicateCando,
    isCandoUniqDirty,
    // OUTPUTS
    color,
    label,
    isOwned,
    iOwnIt,
    isTbd,
    hasEngagement,
    iTbdItWithoutEngagement
  });

  // Return evaluation details
  return {
    color,
    label,
    isOwned,
    iOwnIt,
    isTbd,
    hasEngagement,
    iTbdItWithoutEngagement
  };

};
